import * as React from 'react';
import URI from 'urijs';
import { isEmpty, flatMap } from 'lodash';

// @ts-ignore
import { Target, TARGET_TYPES, OrganizationTarget } from 'entities/Message';
// @ts-ignore
import { allTargetsAreGroups } from 'lib/RoleBasedMessagingUtils';
import Button, { ButtonSize } from 'components/Button';
// @ts-ignore
import ModalTrigger from 'components/ModalTrigger';
import SchedulingDisabledTooltipWithButton from 'chat/components/SchedulingDisabledTooltipWithButton';
// @ts-ignore
import SendToDistrictConfirmDialog from 'chat/components/SendToDistrictConfirmDialog';
// @ts-ignore
import SendUrgentMessageConfirmDialog from 'chat/components/SendUrgentMessageConfirmDialog';
import { UIEventContext } from './SplitComposer/components/ComposerContext';

export type Props = {
  charactersRemaining: number;
  districtIsSelected?: boolean;
  isScheduling: boolean;
  isUrgentMessage?: boolean;
  latestTranslation?: string;
  messageIsValid: boolean;
  onContinueConfirm: () => void;
  onRequestHide?: () => void;
  onSchedule: () => Promise<void>;
  onSend: () => Promise<void>;
  orgName?: string;
  orgUuid?: string;
  targets: Target[];
  totalRecipientCount: number;
  uiEventContext: UIEventContext;
};

export default class VisualComposerPrimaryButton extends React.Component<Props> {
  getTargetFilters = () => {
    const orgTargets = this.props.targets.filter(
      target =>
        target.type === TARGET_TYPES.ORGANIZATION_TARGET &&
        (target as OrganizationTarget).selectedFilters &&
        (target as OrganizationTarget).selectedFilters.length
    );
    return flatMap(orgTargets, orgTarget => (orgTarget as OrganizationTarget).selectedFilters);
  };

  getEventKey = () => {
    const { isScheduling } = this.props;
    return isScheduling
      ? 'announcements.announcement_modal.universal_composer.submit.schedule.click'
      : 'announcements.announcement_modal.universal_composer.submit.send.click';
  };

  getEventProperties = () => {
    const { latestTranslation, uiEventContext } = this.props;
    const currentURL = window.location.href;
    const pre_filled = URI(currentURL).search(true)['message'];
    const share_sdk_pre_filled_message_boolean = !isEmpty(pre_filled);

    return latestTranslation
      ? {
          ...uiEventContext,
          language_code: latestTranslation,
          share_sdk_pre_filled_message: share_sdk_pre_filled_message_boolean,
        }
      : {
          ...uiEventContext,
          share_sdk_pre_filled_message: share_sdk_pre_filled_message_boolean,
        };
  };

  tryingToScheduleSendToFew = () => {
    return this.props.isScheduling && !allTargetsAreGroups(this.props.targets);
  };

  submissionIsDisabled = () => {
    return (
      this.props.charactersRemaining < 0 ||
      !this.props.messageIsValid ||
      this.tryingToScheduleSendToFew()
    );
  };

  renderSendOrScheduleButton = () => {
    const { isScheduling, onSchedule, onSend } = this.props;
    return (
      <Button
        // Need VisualComposer-sendButton for functional tests
        className="VisualComposer-sendButton"
        data-test-selector="visual_composer_send_button"
        level="primary"
        size={ButtonSize.xxsmall}
        weight="normal"
        eventKey={this.getEventKey()}
        eventProperties={this.getEventProperties()}
        disabled={this.submissionIsDisabled()}
        onClick={isScheduling ? onSchedule : onSend}>
        {isScheduling ? 'Schedule' : 'Send'}
      </Button>
    );
  };

  renderContinueButton = () => {
    const { isScheduling, orgUuid, orgName, isUrgentMessage } = this.props;

    const getModal = () => {
      const props = {
        scheduled: isScheduling,
        filters: this.getTargetFilters(),
        orgUuid,
        orgName,
        onConfirm: this.props.onContinueConfirm,
      };
      if (isUrgentMessage) {
        return <SendUrgentMessageConfirmDialog {...props} />;
      } else {
        return <SendToDistrictConfirmDialog {...props} />;
      }
    };

    return (
      <ModalTrigger modal={getModal()}>
        <Button
          level="primary"
          size={ButtonSize.small}
          weight="normal"
          disabled={this.submissionIsDisabled()}
          eventKey="announcements.composer.continue.click"
          data-test-selector="visual_composer_continue_button"
          eventProperties={{
            organization_uuid: orgUuid,
          }}>
          Continue
        </Button>
      </ModalTrigger>
    );
  };

  render() {
    const { totalRecipientCount, districtIsSelected, isUrgentMessage } = this.props;
    if (isUrgentMessage) {
      // if the recipient count is 0, we probably came in from a flow that didn't populate the redux state with the counts.
      // an example is editing a scheduled message.
      // we just skip the confirmation dialog in these cases.
      if (totalRecipientCount > 0) {
        return this.renderContinueButton();
      } else {
        return this.renderSendOrScheduleButton();
      }
    } else if (districtIsSelected) {
      if (totalRecipientCount > 0) {
        return this.renderContinueButton();
      } else {
        return this.renderSendOrScheduleButton();
      }
    } else if (this.tryingToScheduleSendToFew()) {
      return <SchedulingDisabledTooltipWithButton button={this.renderSendOrScheduleButton()} />;
    } else {
      return this.renderSendOrScheduleButton();
    }
  }
}
